import {
  addDestinationCardPaymentMethod,
  tokenizeDestinationCard,
} from "../services/api";
import type { DestinationPayoutInput } from "../interfaces/payout-instrument";
import { brandFromPan, last4 as last4Of } from "./card-brand";

export interface PayoutCardSaved extends DestinationPayoutInput {
  source: "saved";
  paymentMethodId: string;
}

/**
 * Registers a pre-validated destination (push-to-card) instrument via CVS
 * promote (`cvsValidationId`). Reuses the VGS `numberToken` captured on the
 * payout step — no raw PAN after Continue, and no second tokenize when the
 * alias is present.
 */
export const registerPayoutCard = async (
  linkId: string,
  payout: DestinationPayoutInput,
  cardholderName?: string
): Promise<PayoutCardSaved> => {
  const [expiryMonth, expiryYearShort] = (payout.cardExpiry ?? "").split("/");
  const expiryYear = expiryYearShort ? `20${expiryYearShort}` : "";

  const holder =
    cardholderName?.trim() || payout.cardholderName?.trim() || undefined;

  let numberToken = payout.numberToken;
  let last4 = payout.last4 ?? "";
  let brand = (payout.brand || "OTHER").toUpperCase();

  if (!numberToken) {
    const cleanedPan = (payout.cardPan ?? "").replace(/\D/g, "");
    if (cleanedPan.length < 13) {
      throw new Error("PAYOUT_CARD_MISSING_TOKEN");
    }
    brand = brandFromPan(cleanedPan);
    last4 = last4Of(cleanedPan);

    const tokenized = await tokenizeDestinationCard(cleanedPan);
    numberToken = tokenized.numberToken;
  }

  const registered = await addDestinationCardPaymentMethod(linkId, {
    tokenizedCardNumber: numberToken,
    last4,
    brand,
    expiryMonth,
    expiryYear,
    cardholderName: holder,
    validationId: payout.cvsValidationId || undefined,
  });

  return {
    source: "saved",
    paymentMethodId: registered.paymentMethodId,
    brand: (registered.brand || brand).toUpperCase(),
    last4: registered.last4 || last4,
    cardExpiry: payout.cardExpiry,
    cardholderName: registered.cardholderName || holder,
  };
};
